import { Box, SxProps, TextField, TextFieldProps, styled } from '@mui/material';
import { colors } from '@sweep-io/sweep-design';
import { Sparkle } from '@sweep-io/sweep-design/dist/icons';
import { ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { buttons } from './Buttons';
import { getOpacityInHex } from '../../../../lib/getOpacityInHex';
import classNames from 'classnames';
import SelectedTextBox from './SelectedTextBox';

export type TextFieldVariant = 'primary' | 'secondary';

type TextFieldWithButtonProps = TextFieldProps & {
  onConfirm: (message: string) => void;
  inputPlaceholder?: string;
  readonly?: boolean;
  isLoading?: boolean;
  inputVariant?: TextFieldVariant;
  value: string;
  setValue: (value: string) => void;
  selectedText?: string | null;
  onClearSelectedText?: () => void;
};

//This is needed for Markdown to identify it as a line break and not a space.
const handleNewlines = (input: string) => input.replace(/\n/g, '  \n');

export const TextFieldWithButton = ({
  value,
  setValue,
  onConfirm,
  onFocus,
  onBlur,
  inputPlaceholder,
  readonly = false,
  isLoading = false,
  inputVariant = 'primary',
  selectedText,
  onClearSelectedText,
  ...rest
}: TextFieldWithButtonProps) => {
  const isValid = !!value;

  const ButtonComponent = buttons[inputVariant];
  const inputRef = useRef<HTMLInputElement>(null);

  const [focused, setFocused] = useState(false);

  const handleKeyDown = (event: any) => {
    if (!isLoading && event.key === 'Enter' && !event.shiftKey) {
      // If shift is pressed - go down a line.
      // If shift is not pressed - call onConfirm.
      // (default is to go down a line on both cases)
      event.preventDefault();
      _onConfirm();
    }
  };

  //return the focus to the input after getting server response
  useEffect(() => {
    if (!isLoading && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isLoading]);

  //return the focus to the input after adding reply
  useEffect(() => {
    if (selectedText && inputRef.current) {
      inputRef.current.focus();
    }
  }, [selectedText]);

  const _onConfirm = useCallback(() => {
    if (!isValid) {
      return;
    }
    onConfirm(handleNewlines(value));
    setValue('');
  }, [isValid, onConfirm, value, setValue]);

  const handleClearSelectedText = useCallback(() => {
    onClearSelectedText?.();
  }, [onClearSelectedText]);

  return (
    <TextField
      {...rest}
      inputRef={inputRef}
      onKeyDown={handleKeyDown}
      onChange={(event) => setValue(event.target.value)}
      onFocus={(event) => {
        setFocused(true);
        onFocus && onFocus(event);
      }}
      onBlur={(event) => {
        setFocused(false);
        onBlur && onBlur(event);
      }}
      value={value}
      multiline={true}
      className={classNames({ focused: focused })}
      sx={{
        width: '100%',
        borderRadius: '12px',
        boxShadow: 'none',
        ...textFieldVariantStyles[inputVariant],
      }}
      InputProps={{
        className: classNames({ focused: focused }),
        placeholder: inputPlaceholder,
        disabled: readonly || isLoading,
        sx: {
          width: 'unset',
          minHeight: '48px', //to reach input height of 48px, for "secondary" variant
          paddingTop: '10px!important', //to reach input height of 48px, for "primary" variant
          paddingBottom: '10px!important', //to reach input height of 48px, for "primary" variant
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'stretch',
          justifyContent: 'center',
          '.MuiOutlinedInput-input': {
            minHeight: '20px', //to override default, so it will be centered
          },
          ...inputVariantStyles[inputVariant],
        },
        startAdornment: selectedText && (
          <SelectedTextBox selectedText={selectedText} onCloseClick={handleClearSelectedText} />
        ),
        endAdornment: (
          <>
            {inputVariant === 'primary' && (
              <SparkleLocator>
                <Sparkle color={colors.blue[500]} size="medium" />
              </SparkleLocator>
            )}
            <ButtonLocator variant={inputVariant}>
              {ButtonComponent({
                onClick: _onConfirm,
                disabled: isLoading || readonly || !isValid,
              })}
            </ButtonLocator>
          </>
        ),
      }}
    />
  );
};

const SparkleLocator = styled(Box)({
  position: 'absolute',
  left: '15px',
  bottom: '7px',
});

const ButtonLocator = ({
  variant,
  children,
}: {
  variant?: TextFieldVariant;
  children: ReactNode;
}) => (
  <Box
    sx={{
      position: 'absolute',
      right: '12px',
      bottom: variant === 'primary' ? '6px' : '12px',
    }}
  >
    {children}
  </Box>
);

const sharedStyles = {
  borderRadius: '8px',
};

const textFieldVariantStyles: { [key in TextFieldVariant]: SxProps } = {
  primary: {
    border: `4px solid ${colors.blue[500]}${getOpacityInHex(20)}`,

    '&.focused': {
      borderColor: `${colors.blue[500]}${getOpacityInHex(20)}`,
    },

    '&:hover': {
      background: `${colors.blue[500]}${getOpacityInHex(40)}`,
    },
  },
  secondary: {},
};

const inputVariantStyles: { [key in TextFieldVariant]: SxProps } = {
  primary: {
    ...sharedStyles,
    border: `2px solid ${colors.blue[500]}`,

    '& .MuiOutlinedInput-notchedOutline': {
      border: 'none',
    },
    '&.MuiInputBase-adornedEnd': {
      '.MuiInputBase-input': {
        width: 'unset',
        paddingRight: '48px', // leave room for the absolutely positioned button
        paddingLeft: '32px', // leave room for the absolutely positioned Sparkle
      },
    },
  },
  secondary: {
    '&.MuiInputBase-adornedEnd': {
      paddingLeft: '10px',
      '.MuiInputBase-input': {
        width: 'unset',
        paddingRight: '40px', // leave room for the absolutely positioned button
        paddingLeft: '4px', //The input needs to be a bit more inside than the "SelectedTextBox"
      },
    },
    ...sharedStyles,
  },
};
